import {
  Button,
  Card,
  Divider,
  Form,
  Input,
  List,
  Modal,
  Select,
  Space,
  Switch,
  Tooltip,
  Typography,
  message
} from 'antd';
import {
  CarrierInfoDTO,
  CarrierMappingFormData,
  ProducerDTO,
  ProducerFormData,
  UpdateProducerDTO
} from '../../types/data/producer-management.type';
import {
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  PlusOutlined
} from '@ant-design/icons';
import React, { useEffect, useState } from 'react';

import { ProducerService } from '../../service/producer-management.service';

const { Option } = Select;
const { Title } = Typography;

interface EditProducerModalProps {
  visible: boolean;
  producer: ProducerDTO | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const EditProducerModal: React.FC<EditProducerModalProps> = ({
  visible,
  producer,
  onCancel,
  onSuccess
}) => {
  const [form] = Form.useForm<ProducerFormData>();
  const [loading, setLoading] = useState(false);
  const [availableCarriers, setAvailableCarriers] = useState<CarrierInfoDTO[]>(
    []
  );
  const [carrierMappings, setCarrierMappings] = useState<
    CarrierMappingFormData[]
  >([]);
  const [selectedCarrier, setSelectedCarrier] = useState<string>('');
  const [agentId, setAgentId] = useState<string>('');
  const [editingMapping, setEditingMapping] = useState<string | null>(null);
  const [editAgentId, setEditAgentId] = useState<string>('');

  const loadAvailableCarriers = async () => {
    try {
      const carriers = await ProducerService.getAvailableCarriers();
      setAvailableCarriers(carriers);
    } catch (error) {
      message.error('Failed to load available carriers');
    }
  };

  useEffect(() => {
    if (visible && producer) {
      loadAvailableCarriers();

      // Set form values
      form.setFieldsValue({
        name: producer.name,
        npnNumber: producer.npnNumber,
        isProducerActiveForEApp: producer.isProducerActiveForEApp
      });

      // Set carrier mappings
      const mappings = producer.carrierMappings.map(mapping => ({
        carrierNaic: mapping.carrierNaic,
        agentId: mapping.agentId || ''
      }));
      setCarrierMappings(mappings);
    }
  }, [visible, producer, form]);

  const handleAddCarrierMapping = () => {
    if (!selectedCarrier || !agentId.trim()) {
      message.warning('Please select a carrier and enter an agent ID');
      return;
    }

    // Check if carrier already added
    if (
      carrierMappings.some(mapping => mapping.carrierNaic === selectedCarrier)
    ) {
      message.warning('This carrier has already been added');
      return;
    }

    setCarrierMappings([
      ...carrierMappings,
      {
        carrierNaic: selectedCarrier,
        agentId: agentId.trim()
      }
    ]);
    setSelectedCarrier('');
    setAgentId('');
  };

  const handleRemoveCarrierMapping = (carrierNaic: string) => {
    setCarrierMappings(
      carrierMappings.filter(mapping => mapping.carrierNaic !== carrierNaic)
    );
  };

  const handleEditMapping = (carrierNaic: string, currentAgentId: string) => {
    setEditingMapping(carrierNaic);
    setEditAgentId(currentAgentId);
  };

  const handleSaveEdit = (carrierNaic: string) => {
    if (!editAgentId.trim()) {
      message.warning('Please enter an agent ID');
      return;
    }

    setCarrierMappings(
      carrierMappings.map(mapping =>
        mapping.carrierNaic === carrierNaic
          ? { ...mapping, agentId: editAgentId.trim() }
          : mapping
      )
    );
    setEditingMapping(null);
    setEditAgentId('');
  };

  const handleCancelEdit = () => {
    setEditingMapping(null);
    setEditAgentId('');
  };

  const handleSubmit = async (values: ProducerFormData) => {
    if (!producer) return;

    setLoading(true);
    try {
      const updateData: UpdateProducerDTO = {
        name: values.name,
        npnNumber: values.npnNumber,
        isProducerActiveForEApp: values.isProducerActiveForEApp,
        carrierMappings: carrierMappings.map(mapping => ({
          carrierNaic: mapping.carrierNaic,
          agentId: mapping.agentId
        }))
      };

      await ProducerService.updateProducer(producer.id, updateData);
      message.success('Producer updated successfully');
      onSuccess();
    } catch (error: any) {
      message.error(
        error.response?.data?.message || 'Failed to update producer'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setCarrierMappings([]);
    setSelectedCarrier('');
    setAgentId('');
    setEditingMapping(null);
    setEditAgentId('');
    onCancel();
  };

  const getCarrierName = (naic: string) => {
    const carrier = availableCarriers.find(c => c.naic === naic);
    return carrier ? carrier.name : naic;
  };

  if (!producer) {
    return null;
  }

  return (
    <Modal
      title={`Edit Producer: ${producer.name}`}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={800}
      destroyOnClose>
      <Form form={form} layout='vertical' onFinish={handleSubmit}>
        <Form.Item
          label='Producer Name'
          name='name'
          rules={[
            { required: true, message: 'Please enter producer name' },
            { min: 2, message: 'Name must be at least 2 characters' }
          ]}>
          <Input placeholder='Enter producer name' />
        </Form.Item>

        <Form.Item
          label='NPN Number'
          name='npnNumber'
          rules={[
            { required: true, message: 'Please enter NPN number' },
            { pattern: /^[0-9]+$/, message: 'NPN must contain only numbers' }
          ]}>
          <Input placeholder='Enter NPN number' />
        </Form.Item>

        <Form.Item
          label={
            <Space>
              Active for E-App
              <Tooltip title='Active producer will be used for E-App submissions. Only one producer can be active at a time.'>
                <InfoCircleOutlined />
              </Tooltip>
            </Space>
          }
          name='isProducerActiveForEApp'
          valuePropName='checked'>
          <Switch />
        </Form.Item>

        <Divider />

        <Title level={5}>Carriers Contracted</Title>

        <Card size='small' style={{ marginBottom: 16 }}>
          <Space.Compact style={{ width: '100%' }}>
            <Select
              placeholder='Select carrier'
              style={{ width: '40%' }}
              value={selectedCarrier}
              onChange={setSelectedCarrier}
              showSearch
              optionFilterProp='children'>
              {availableCarriers
                .filter(
                  carrier =>
                    !carrierMappings.some(
                      mapping => mapping.carrierNaic === carrier.naic
                    )
                )
                .map(carrier => (
                  <Option key={carrier.naic} value={carrier.naic}>
                    {carrier.name} ({carrier.naic})
                  </Option>
                ))}
            </Select>
            <Input
              placeholder='Enter agent ID'
              style={{ width: '40%' }}
              value={agentId}
              onChange={e => setAgentId(e.target.value)}
              onPressEnter={handleAddCarrierMapping}
            />
            <Button
              type='primary'
              icon={<PlusOutlined />}
              onClick={handleAddCarrierMapping}
              style={{ width: '20%' }}>
              Add
            </Button>
          </Space.Compact>
        </Card>

        {carrierMappings.length > 0 && (
          <Card
            title='Carriers Contracted'
            size='small'
            style={{ marginBottom: 16 }}>
            <List
              size='small'
              dataSource={carrierMappings}
              renderItem={mapping => (
                <List.Item
                  actions={[
                    <Button
                      key={`edit-${mapping.carrierNaic}`}
                      type='text'
                      icon={<EditOutlined />}
                      onClick={() =>
                        handleEditMapping(mapping.carrierNaic, mapping.agentId)
                      }
                      disabled={editingMapping === mapping.carrierNaic}
                    />,
                    <Button
                      key={`delete-${mapping.carrierNaic}`}
                      type='text'
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() =>
                        handleRemoveCarrierMapping(mapping.carrierNaic)
                      }
                      disabled={editingMapping === mapping.carrierNaic}
                    />
                  ]}>
                  <List.Item.Meta
                    title={getCarrierName(mapping.carrierNaic)}
                    description={
                      editingMapping === mapping.carrierNaic ? (
                        <Space.Compact style={{ width: '300px' }}>
                          <Input
                            value={editAgentId}
                            onChange={e => setEditAgentId(e.target.value)}
                            placeholder='Agent ID'
                            onPressEnter={() =>
                              handleSaveEdit(mapping.carrierNaic)
                            }
                          />
                          <Button
                            type='primary'
                            size='small'
                            onClick={() => handleSaveEdit(mapping.carrierNaic)}>
                            Save
                          </Button>
                          <Button size='small' onClick={handleCancelEdit}>
                            Cancel
                          </Button>
                        </Space.Compact>
                      ) : (
                        `NAIC: ${mapping.carrierNaic} | Agent ID: ${mapping.agentId}`
                      )
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        )}

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button type='primary' htmlType='submit' loading={loading}>
              Update Producer
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditProducerModal;
